import React, {forwardRef, ReactElement, useImperativeHandle, useRef} from 'react';
import {Entity} from '../../types/entities/Entity';
import Drawer from '@mui/material/Drawer';
import {SchemaWithClient} from '../../types/SchemaWithClient';
import 'react-tabs/style/react-tabs.css';
import MinimizedContent from '../SidePane/MinimizedContent';
import CompleteContent from '../SidePane/CompleteContent';

type Props<T extends Entity> = {
  schema: SchemaWithClient<T>;
  id: number | undefined | string;
  onSubmit: (item: any, dialog?: string) => void;
  result?: (item: Partial<T>) => void;
  onClose: (item: any) => void;
  properties?: Partial<T>;
  propertiesFromTable: any;
  row?: any;
  hasSlidingPane?: boolean;
  addNewRow?: (row: any, tab: any, multiselect?: boolean) => void;
  setFullWidthProp: (value: boolean) => void;
  hasUnsavedModifications?: (value: any) => void;
  forcedRefresh?: () => void;
  refreshMainTable?: () => void;
  fullWidth: boolean;
  isDialog?: boolean;
  isLocked?: any;
  isInFullPanel?: boolean;
};

interface SlidingPaneHandles {
  onSubmit: (item: any, dialog?: string) => void;
}

export const SlidingPane = forwardRef<SlidingPaneHandles, Props<any>>(
  (
    {
      schema,
      onSubmit: handleSubmit,
      id,
      result,
      propertiesFromTable,
      row,
      onClose,
      hasSlidingPane,
      addNewRow,
      setFullWidthProp,
      fullWidth,
      refreshMainTable,
      isDialog,
      forcedRefresh,
      isLocked,
      hasUnsavedModifications,
      isInFullPanel,
    }: Props<any>,
    ref
  ): ReactElement => {

    const completeContentRef = useRef<any>(null);
    const minimizedContentRef = useRef<any>(null);

    useImperativeHandle(ref, () => ({
      onSubmit: (value: any, dialog?: string) => {
        if (completeContentRef.current?.handleSubmit) {
          completeContentRef.current.handleSubmit(value, dialog);
        }
        if (minimizedContentRef.current?.handleSubmit) {
          minimizedContentRef.current.handleSubmit(value, dialog);
        }
      },
    }));

    return (
      <>
        <Drawer
          onClose={() => {
            onClose(true);
          }}
          open={true}
          anchor={'right'}
          hideBackdrop={true}
          sx={{
            '& > .MuiPaper-root': {
              zIndex: 10,
              borderTop: '1px solid #999',
              borderBottom: '1px solid #dfdbdb',
              overflow: 'hidden',
              height: `calc(100vh - ${isDialog ? '164px' : '0px'})`,
              position: 'relative',
              background: fullWidth ? (isInFullPanel ? '#f7f7f7':'#f1f1f1') : '',
            },
            '& .MuiInputBase-root.MuiOutlinedInput-root': {
              borderRadius: '0px',
            },
            '& .MuiInputBase-root.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
              borderColor: 'transparent',
            },
            '& .MuiInputBase-root.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#9c1111',
            },
            '& .MuiInputBase-root.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#9c1111',
            },
          }}
          variant={'persistent'}
        >
          {fullWidth ? (
            <CompleteContent
              ref={completeContentRef}
              schema={schema}
              propertiesFromTable={propertiesFromTable}
              handleSubmit={(value: any, dialog?: string) => {
                handleSubmit(value, dialog);
              }}
              onChangeFullWidth={(value:any) => {
                setFullWidthProp(value);
              }}
              hasSlidingPane={hasSlidingPane}
              onClose={(value:any) => {
                onClose(value);
              }}
              forcedRefresh={() => {
                if (forcedRefresh) {
                  forcedRefresh();
                }
              }}
              id={id}
              isLocked={isLocked}
              isInFullPanel={isInFullPanel}
            />
          ) : (
            <MinimizedContent
              ref={minimizedContentRef}
              schema={schema}
              isDialog={isDialog}
              propertiesFromTable={propertiesFromTable}
              handleSubmit={(value: any, dialog?: string) => {
                handleSubmit(value, dialog);
              }}
              onChangeFullWidth={(value:any) => {
                setFullWidthProp(value);
              }}
              hasSlidingPane={hasSlidingPane}
              onClose={(item:any) => {
                onClose(undefined);
              }}
              id={id}
              hasFullScreen={true}
              refreshMainTable={() => {
                if (refreshMainTable) {
                  refreshMainTable();
                }
              }}
              hasUnsavedModifications={(value:any) => {
                if (hasUnsavedModifications){
                  hasUnsavedModifications(value);
                }
              }}
              isLocked={isLocked}
            />
          )}
        </Drawer>
      </>
    );
  });
